import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BudgetBucket, SubCategory } from "./types/budget.types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { BarChart, PieChart, Plus, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";

// Import refactored components
import { PieChartComponent } from "./components/charts/PieChartComponent";
import { BarChartComponent } from "./components/charts/BarChartComponent";
import { TreemapComponent } from "./components/charts/TreemapComponent";
import { SubcategoryTable } from "./components/subcategory/SubcategoryTable";
import { SubcategoryDialogs } from "./components/subcategory/SubcategoryDialogs";
import { prepareBudgetChartData } from "./components/charts/BudgetChartData";

interface BudgetAllocationProps {
  totalBudget: number;
  buckets: BudgetBucket[];
  onAddSubcategory: (bucketId: string, name: string, budget: number, expRomi: string, kpi: string, description: string, expCompletion: string | null) => Promise<void>;
  onUpdateSubcategory: (bucketId: string, subcategoryId: string, name: string, budget: number, expRomi: string, kpi: string, description: string, expCompletion: string | null) => Promise<void>;
  onDeleteSubcategory: (bucketId: string, subcategoryId: string) => Promise<void>;
}

export const BudgetAllocation: React.FC<BudgetAllocationProps> = ({
  totalBudget,
  buckets,
  onAddSubcategory,
  onUpdateSubcategory,
  onDeleteSubcategory
}) => {
  const [chartType, setChartType] = useState<'pie' | 'bar' | 'treemap'>('pie');
  const [selectedBucketId, setSelectedBucketId] = useState<string>("");
  const [selectedSubcategory, setSelectedSubcategory] = useState<SubCategory | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState<boolean>(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    budget: "",
    expRomi: "",
    kpi: "",
    description: "",
    expCompletion: ""
  });

  // Format input with commas as user types
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Special handling for budget to format with commas as user types
    if (name === 'budget' && value) {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      // Format with commas
      const formattedValue = digitsOnly ? parseInt(digitsOnly).toLocaleString() : '';
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      // Handle other fields normally
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Open the add subcategory dialog
  const openAddDialog = (bucketId: string) => {
    setSelectedBucketId(bucketId);
    setFormData({
      name: "",
      budget: "",
      expRomi: "",
      kpi: "",
      description: "",
      expCompletion: ""
    });
    setIsAddDialogOpen(true);
  };

  // Open the edit subcategory dialog
  const openEditDialog = (bucketId: string, subcategory: SubCategory) => {
    setSelectedBucketId(bucketId);
    setSelectedSubcategory(subcategory);
    setFormData({
      name: subcategory.name,
      budget: subcategory.budget.toLocaleString(),
      // Format with commas for display
      expRomi: subcategory.expRomi || "",
      kpi: subcategory.kpi || "",
      description: subcategory.description || "",
      expCompletion: subcategory.expCompletion || ""
    });
    setIsEditDialogOpen(true);
  };

  // Open the delete subcategory dialog
  const openDeleteDialog = (bucketId: string, subcategory: SubCategory) => {
    setSelectedBucketId(bucketId);
    setSelectedSubcategory(subcategory);
    setIsDeleteDialogOpen(true);
  };

  // Open the subcategory details dialog
  const openDetailsDialog = (bucketId: string, subcategory: SubCategory) => {
    setSelectedBucketId(bucketId);
    setSelectedSubcategory(subcategory);
    setIsDetailsDialogOpen(true);
  };

  // Handle adding a new subcategory
  const handleAddSubcategory = async () => {
    // Parse budget by removing commas first
    const budgetStr = formData.budget.replace(/,/g, '');
    const budget = parseInt(budgetStr);
    if (!selectedBucketId || !formData.name || isNaN(budget) || budget <= 0) {
      return;
    }

    // Get selected bucket
    const selectedBucket = buckets.find(b => b.id === selectedBucketId);
    if (!selectedBucket) return;

    // Check if budget exceeds available amount
    if (budget > selectedBucket.available) {
      setAlertMessage(`The requested budget of $${budget.toLocaleString()} exceeds the available budget of $${selectedBucket.available.toLocaleString()}.`);
      setIsAlertDialogOpen(true);
      return;
    }
    await onAddSubcategory(selectedBucketId, formData.name, budget, formData.expRomi, formData.kpi, formData.description, formData.expCompletion || null);

    // Keep the selectedBucketId unchanged to maintain the dropdown selection
    // Clear the form but keep the dialog open and the bucket selected
    setFormData({
      name: "",
      budget: "",
      expRomi: "",
      kpi: "",
      description: "",
      expCompletion: ""
    });
    setIsAddDialogOpen(false);
  };

  // Handle updating a subcategory
  const handleUpdateSubcategory = async () => {
    // Parse budget by removing commas first
    const budgetStr = formData.budget.replace(/,/g, '');
    const budget = parseInt(budgetStr);
    if (!selectedBucketId || !selectedSubcategory || !formData.name || isNaN(budget) || budget <= 0) {
      return;
    }
    await onUpdateSubcategory(selectedBucketId, selectedSubcategory.id, formData.name, budget, formData.expRomi, formData.kpi, formData.description, formData.expCompletion || null);
    setIsEditDialogOpen(false);
  };

  // Handle deleting a subcategory
  const handleDeleteSubcategory = async () => {
    if (!selectedBucketId || !selectedSubcategory) {
      return;
    }
    await onDeleteSubcategory(selectedBucketId, selectedSubcategory.id);
    setIsDeleteDialogOpen(false);
  };

  // Sort buckets alphabetically
  const sortedBuckets = [...buckets].sort((a, b) => a.name.localeCompare(b.name));

  // Prepare data for charts
  const bucketChartData = prepareBudgetChartData(buckets);

  // Get selected bucket
  const selectedBucket = buckets.find(b => b.id === selectedBucketId);

  // Prepare subcategory data for selected bucket
  const subcategoryChartData = selectedBucket?.subcategories 
    ? prepareBudgetChartData(selectedBucket.subcategories)
    : [];

  return (
    <div className="space-y-6">
      <Card className="bg-secondary/10 border-2 border-primary">
        <CardHeader className="">
          <CardTitle className="text-primary my-[8px]">Budget Allocation Visualization</CardTitle>
          <div className="flex flex-col md:flex-row justify-between gap-4 mt-2">
            <div className="flex items-center gap-2">
              <Label className="text-white">Select Marketing Bucket:</Label>
              <Select value={selectedBucketId} onValueChange={setSelectedBucketId}>
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="Select Bucket" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black">
                  {sortedBuckets.map(bucket => (
                    <SelectItem key={bucket.id} value={bucket.id}>{bucket.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={chartType === 'pie' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setChartType('pie')} 
                className="rounded-full w-10 h-10 p-0"
              >
                <PieChart className="h-5 w-5" />
              </Button>
              <Button 
                variant={chartType === 'bar' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setChartType('bar')} 
                className="rounded-full w-10 h-10 p-0"
              >
                <BarChart className="h-5 w-5" />
              </Button>
              <Button 
                variant={chartType === 'treemap' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setChartType('treemap')} 
                className="rounded-full w-10 h-10 p-0"
              >
                <LayoutGrid className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-2/5">
              {totalBudget === 0 ? (
                <div className="flex items-center justify-center h-[300px] bg-secondary/20 rounded-md">
                  <p className="text-center text-primary-light">
                    No budget data available. Please set a total budget first.
                  </p>
                </div>
              ) : buckets.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] bg-secondary/20 rounded-md">
                  <p className="text-center text-primary-light">
                    No budget buckets defined yet. Please add budget buckets first.
                  </p>
                </div>
              ) : (
                <div className="h-[330px] w-full text-[11px]">
                  {chartType === 'pie' && (
                    <PieChartComponent 
                      data={selectedBucketId && selectedBucket && selectedBucket.subcategories.length > 0 
                        ? subcategoryChartData 
                        : bucketChartData} 
                    />
                  )}
                  {chartType === 'bar' && (
                    <BarChartComponent 
                      data={selectedBucketId && selectedBucket && selectedBucket.subcategories.length > 0 
                        ? subcategoryChartData 
                        : bucketChartData} 
                    />
                  )}
                  {chartType === 'treemap' && (
                    <TreemapComponent 
                      data={selectedBucketId && selectedBucket && selectedBucket.subcategories.length > 0 
                        ? subcategoryChartData 
                        : bucketChartData} 
                    />
                  )}
                </div>
              )}
            </div>
            
            {selectedBucketId && selectedBucket ? (
              <div className="w-full md:w-3/5 border-2 border-white p-4 rounded-md">
                <div className="mb-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-primary">{selectedBucket.name}</h3>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => openAddDialog(selectedBucket.id)} 
                      className="h-8 gap-1 bg-primary border-none"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Subcategory</span>
                    </Button>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-primary-light">Budget breakdown for {selectedBucket.name}</span>
                    <span className="font-medium text-primary-light">
                      Total: ${selectedBucket.budget.toLocaleString()} | Available: ${selectedBucket.available.toLocaleString()}
                    </span>
                  </div>
                </div>
                
                {selectedBucket.subcategories.length === 0 ? (
                  <div className="text-center py-8 text-primary-light border border-dashed border-secondary/30 rounded-md">
                    <p>No subcategories defined for this budget bucket yet.</p>
                    <Button 
                      variant="link" 
                      className="mt-2 text-primary" 
                      onClick={() => openAddDialog(selectedBucket.id)}
                    >
                      Add a subcategory
                    </Button>
                  </div>
                ) : (
                  <SubcategoryTable
                    subcategories={selectedBucket.subcategories}
                    openDetailsDialog={openDetailsDialog}
                    openEditDialog={openEditDialog}
                    openDeleteDialog={openDeleteDialog}
                    bucketId={selectedBucket.id}
                  />
                )}
              </div>
            ) : (
              <div className="w-full md:w-3/5 border border-white p-4 rounded-md">
                <div className="text-center py-8 text-primary-light">
                  <p>Select a budget bucket to manage its subcategories.</p>
                  <p className="mt-2">The chart on the left shows the overall budget allocation across all buckets.</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      <SubcategoryDialogs
        selectedBucket={selectedBucket}
        selectedSubcategory={selectedSubcategory}
        formData={formData}
        isAddDialogOpen={isAddDialogOpen}
        isEditDialogOpen={isEditDialogOpen}
        isDeleteDialogOpen={isDeleteDialogOpen}
        isDetailsDialogOpen={isDetailsDialogOpen}
        isAlertDialogOpen={isAlertDialogOpen}
        alertMessage={alertMessage}
        handleFormChange={handleFormChange}
        handleAddSubcategory={handleAddSubcategory}
        handleUpdateSubcategory={handleUpdateSubcategory}
        handleDeleteSubcategory={handleDeleteSubcategory}
        setIsAddDialogOpen={setIsAddDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        setIsDetailsDialogOpen={setIsDetailsDialogOpen}
        setIsAlertDialogOpen={setIsAlertDialogOpen}
        openEditDialog={openEditDialog}
      />
    </div>
  );
};
